import { NzNotificationService } from 'ng-zorro-antd/notification';
import { debounceTime, filter, fromEvent, interval, merge } from 'rxjs';

import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';

@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {
  private swUpdate = inject(SwUpdate);
  private notification = inject(NzNotificationService);
  private destroyRef = inject(DestroyRef);

  private initialized = false;
  private autoUpdateTimeoutId: number | null = null;

  readonly isUpdating = signal(false);
  readonly updateAvailable = signal(false);

  async load(): Promise<void> {
    if (!this.swUpdate.isEnabled || this.initialized) {
      return;
    }

    this.initialized = true;

    this.listenForVersionReady();
    this.listenForUnrecoverableState();
    this.listenForUpdateTriggers();

    setTimeout(() => {
      void this.safeCheckForUpdate();
    });
  }

  private listenForVersionReady(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter(event => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.startMandatoryUpdateFlow();
      });
  }

  private listenForUnrecoverableState(): void {
    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.startMandatoryUpdateFlow();
    });
  }

  private listenForUpdateTriggers(): void {
    merge(
      fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden)),
      fromEvent(window, 'focus'),
      interval(5 * 60 * 1000)
    )
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.safeCheckForUpdate();
      });
  }

  private async safeCheckForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // ignora erro silenciosamente
    }
  }

  private startMandatoryUpdateFlow(): void {
    if (this.updateAvailable() || this.isUpdating()) {
      return;
    }

    this.updateAvailable.set(true);

    this.autoUpdateTimeoutId = window.setTimeout(() => {
      void this.applyMandatoryUpdate();
    }, 3000);
  }

  async applyMandatoryUpdate(): Promise<void> {
    if (this.isUpdating()) {
      return;
    }

    if (this.autoUpdateTimeoutId) {
      window.clearTimeout(this.autoUpdateTimeoutId);
      this.autoUpdateTimeoutId = null;
    }

    this.isUpdating.set(true);

    this.notification.info('Atualizando aplicação...', 'Uma nova versão foi publicada. A página será recarregada automaticamente.', {
      nzDuration: 3000,
      nzPlacement: 'topRight'
    });

    try {
      await this.swUpdate.activateUpdate();
    } catch (error) {
      console.error('Erro ao ativar atualização:', error);
    }

    await this.hardReload();
  }

  private async hardReload(): Promise<void> {
    // try {
    //   if ('caches' in window) {
    //     const cacheKeys = await caches.keys();
    //     await Promise.all(cacheKeys.map(key => caches.delete(key)));
    //   }
    // } catch (error) {
    //   console.error('Erro ao limpar caches:', error);
    // } finally {
    //   window.location.reload();
    // }

    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.replace(url.toString());

  }
}
